/**
 * UTM Campaign Parameter Stash
 *
 * Marketing links land on the public auth pages (or the SPA) with
 * `?utm_source=...` etc., but PostHog capture stays off until the visitor
 * grants analytics consent, and the post-login redirect drops the query
 * string. So the UTMs are read from the landing URL and kept in first-party
 * sessionStorage; utils/posthog.ts only hands them to PostHog once consent is
 * granted. Nothing here talks to PostHog.
 *
 * The stash is tab-scoped (sessionStorage) and survives the full-page hop from
 * the Devise pages to the SPA because both are served from the same origin.
 * A landing URL that carries new UTMs overwrites the stash (latest campaign
 * wins); one without UTMs leaves it untouched.
 */

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = "activesnap_utm";
const MAX_VALUE_LENGTH = 200;

/**
 * Extract the UTM parameters from a query string.
 *
 * @param search - a location.search string, with or without the leading `?`
 * @returns the non-empty UTM values, or null when none are present
 */
export function parseUtmParams(search: string): UtmParams | null {
  const params = new URLSearchParams(search);
  const utm: UtmParams = {};

  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) {
      utm[key] = value.slice(0, MAX_VALUE_LENGTH);
    }
  }

  return Object.keys(utm).length > 0 ? utm : null;
}

/**
 * Stash the UTMs from the given query string, if it carries any.
 *
 * @param search - a location.search string
 */
export function stashUtmParams(search: string): void {
  const utm = parseUtmParams(search);
  if (!utm) {
    return;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  } catch {
    // Storage blocked (private mode, disabled site data): attribution is lost.
  }
}

/**
 * Read the stashed UTMs.
 *
 * @returns the stashed UTM values, or null when nothing usable is stored
 */
export function readStashedUtm(): UtmParams | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const utm: UtmParams = {};
    for (const key of UTM_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === "string" && value) {
        utm[key] = value;
      }
    }
    return Object.keys(utm).length > 0 ? utm : null;
  } catch {
    return null;
  }
}

/**
 * Map UTMs to PostHog's first-touch person property names
 * (`utm_source` -> `$initial_utm_source`), for use with `$set_once`.
 *
 * @param utm - the UTM values
 */
export function toInitialUtmProperties(utm: UtmParams): Record<string, string> {
  const props: Record<string, string> = {};
  for (const [key, value] of Object.entries(utm)) {
    props[`$initial_${key}`] = value;
  }
  return props;
}
